import {Fragment, MouseEvent, ReactElement, useState} from 'react'
import {Trans} from 'react-i18next'
import IconButton from '@material-ui/core/IconButton'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Typography from '@material-ui/core/Typography'

import messages from '../../../../../assets/messages'
import {TauroRulesModal} from '../../../../Common/TauroRulesModal'
import {
  getTauroFactorisationLeverage,
  TauroAccountFactorisation,
} from '../../../../../common/utils/accounts'
import {useStyles} from './styles'

type TauroFactorisationDetailsProps = {
  factorisation: TauroAccountFactorisation
}

/**
 * The 10x parameters on the account tile plus the info icon that opens the full rules
 * (PT-6334). Everything comes from the snapshot the account was opened with, so a client
 * always reads the terms their own account runs under, not the current campaign config.
 */
function TauroFactorisationDetails(props: TauroFactorisationDetailsProps): ReactElement {
  const {factorisation} = props
  const classes = useStyles()
  const [rulesOpen, setRulesOpen] = useState(false)

  const leverage = getTauroFactorisationLeverage(factorisation)
  const parameters = [
    factorisation.multiplier != null && (
      <Trans {...messages.tauroTileMultiplier} values={{multiplier: factorisation.multiplier}} />
    ),
    factorisation.drawdownTriggerPercent != null && (
      <Trans
        {...messages.tauroTileDrawdown}
        values={{drawdown: factorisation.drawdownTriggerPercent}}
      />
    ),
    leverage != null && <Trans {...messages.tauroTileLeverage} values={{leverage}} />,
  ].filter(Boolean)

  // The tile itself opens the account on click, and the pop up renders inside it, so every
  // click that belongs to the rules has to stop before it reaches the card.
  const keepClickOnTheRules = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <div className={classes.container} onClick={keepClickOnTheRules}>
      {parameters.map((parameter, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <Typography variant="caption" className={classes.separator}>
              ·
            </Typography>
          )}
          <Typography variant="caption" className={classes.parameter}>
            {parameter}
          </Typography>
        </Fragment>
      ))}
      <IconButton
        className={classes.infoButton}
        aria-label={messages.tauroTileRulesButton.defaults}
        onClick={() => setRulesOpen(true)}
      >
        <InfoOutlinedIcon className={classes.infoIcon} />
      </IconButton>
      <TauroRulesModal
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        rules={factorisation}
      />
    </div>
  )
}

export {TauroFactorisationDetails}
